import { daysFromNow, parse, isValid, DATE_FORMAT } from '@eneco/common/date';
import { useFormatter } from '@eneco/i18n';
import { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import {
  getMinContractStartDateInDays,
  getMaxContractStartDateInDays,
  getMaxUrgentContractStartDateInDays,
  CONTRACT_CONSIDERATION_PERIOD_IN_DAYS,
} from '../../../../shared/helpers';
import { DeliveryDateFormFieldsB } from '../utils/deliveryDateOptionSchema_B';

interface UseDeliveryDateOptionsParams {
  isMoving?: boolean;
  isSME?: boolean;
  label: DC_Repositories_Base_Enumerations_Label;
}

/**
 * The date maths behind the three exp-n-630 option cards: the windows the pickers may select from,
 * the date the `Zo snel mogelijk` card promises, and the mapping from the selected option back to
 * the single `deliveryDate` the flow context stores.
 *
 * Everything here is pure derivation — the variant keeps no date state of its own, so the schema,
 * the rendered cards, and the submitted context can never disagree about which date is current.
 */
export const useDeliveryDateOptions_B = ({ isMoving, isSME, label }: UseDeliveryDateOptionsParams) => {
  const { date } = useFormatter();

  // The same three windows the control derives, with the same arguments — see DeliveryDateStep.tsx.
  const minimumDays = getMinContractStartDateInDays(!!isMoving, !!isMoving, label);
  const maximumDays = getMaxContractStartDateInDays(label);
  const maximumUrgentDays = getMaxUrgentContractStartDateInDays(label);
  const asapDays = getMinContractStartDateInDays(!!isMoving, false, label);

  const minimumDate = daysFromNow(minimumDays);
  const maximumDate = daysFromNow(maximumDays);
  const asapDate = daysFromNow(asapDays);

  /** What `ASAP` submits, and the fallback for a stale restored `CUSTOM` date. */
  const asapDateValue = date.short(asapDate);
  /** Human-readable earliest start date for the card subtitle, e.g. `27 juli 2026`. */
  const asapDateLabel = date.long(asapDate);

  const parseIfValid = (value?: string) => {
    if (!value) return undefined;

    const parsed = parse(value, DATE_FORMAT);

    return isValid(parsed) ? parsed : undefined;
  };

  /**
   * Whether a date coming back from the flow context is still selectable. A customer who navigates
   * back after the window has moved on must not be shown a pre-filled invalid date.
   *
   * Bounded by the pickers' own window (`minimumDate`..`maximumDate`), not by the earliest start
   * date: moving customers may pick from today, so a date sooner than the `ASAP` date is legitimate
   * for them and must survive the round trip — it is exactly the case the consent checkbox exists for.
   */
  const isWithinStartWindow = (value?: string) => {
    const parsed = parseIfValid(value);

    return !!parsed && parsed >= minimumDate && parsed <= maximumDate;
  };

  /**
   * The same check for a contract *end* date, which is deliberately exempt from the start minimum:
   * its picker opens from today and the schema imposes no minimum, so any date from today onwards is
   * a date the customer was allowed to enter and must therefore survive back navigation. Using the
   * start window here would silently wipe an early end date for customers who are not moving.
   */
  const isWithinEndDateWindow = (value?: string) => {
    const parsed = parseIfValid(value);

    return !!parsed && parsed >= daysFromNow(0) && parsed <= maximumDate;
  };

  /**
   * The context's `deliveryDate` for the selected option. `CONTRACT_END` submits the entered end
   * date 1:1 — the card's copy promises delivery *on* the end date.
   */
  const resolveDeliveryDate = ({
    deliveryDateOption,
    contractEndDate,
    customStartDate,
  }: DeliveryDateFormFieldsB): string | undefined => {
    switch (deliveryDateOption) {
      case 'ASAP':
        return asapDateValue;
      case 'CONTRACT_END':
        return contractEndDate || undefined;
      case 'CUSTOM':
        return customStartDate || undefined;
      default:
        return undefined;
    }
  };

  const isUrgentDeliveryDate = (deliveryDate?: string) => {
    const parsed = parseIfValid(deliveryDate);

    return !!parsed && parsed <= daysFromNow(maximumUrgentDays);
  };

  /**
   * An end date before the earliest date we can start is expected rather than invalid — the card
   * reassures the customer instead of blocking them.
   */
  const isEndDateBeforeEarliestStart = (contractEndDate?: string) => {
    const parsed = parseIfValid(contractEndDate);

    return !!parsed && parsed < asapDate;
  };

  /** Moving, non-SME customers must waive their cooling-off period to start inside it. */
  const isConsentEligible = !!isMoving && !isSME;

  const isWithinConsiderationPeriod = (deliveryDate?: string) => {
    const parsed = parseIfValid(deliveryDate);

    return !!parsed && parsed <= daysFromNow(CONTRACT_CONSIDERATION_PERIOD_IN_DAYS);
  };

  return {
    minimumDays,
    maximumDays,
    minimumDate,
    maximumDate,
    asapDate,
    asapDateValue,
    asapDateLabel,
    isWithinStartWindow,
    isWithinEndDateWindow,
    resolveDeliveryDate,
    isUrgentDeliveryDate,
    isEndDateBeforeEarliestStart,
    isConsentEligible,
    isWithinConsiderationPeriod,
  };
};
